import React from "react";
import {
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Grid,
    Modal,
    Paper,
    Stack,
} from "@mui/material";
import styled from "styled-components";
import SettingsApplicationsIcon from "@mui/icons-material/SettingsApplications";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { useAuth } from "src/components/providers/auth-context";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
};

const H = styled.h1`
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 0;
`;

const P = styled.p`
    font-size: 12px;
    font-weight: 300;
    max-width: 95%;
    color: ${({ theme }) => theme.palette.secondary.main};
    padding-bottom: 16px;
    margin: 0;
`;

const SummarySettingsModal = ({ open, handleClose, getLatestSummary }) => {
    const [summarySettings, setSummarySettings] = React.useState([]);
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);

    const { isDemo, isUserPayingCustomer } = useAuth();

    const getSummarySettings = async () => {
        let response;
        if (isDemo || !isUserPayingCustomer) {
            response = (await import("src/scenes/summary/summary-settings-mock-data")).default;
        } else {
            response = await request(`api/users/me/summary-settings`, {
                method: "GET",
            });
        }
        setSummarySettings(response);
        return null;
    };

    const submitSettings = async () => {
        await request(`api/users/me/summary-settings`, {
            method: "POST",
            body: JSON.stringify(summarySettings),
        });
        setMessage(`Settings saved successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
        handleClose();
        getLatestSummary();
    };

    const handleSubmitSettings = async () => {
        try {
            submitSettings();
        } catch (e) {
            setMessage(`Error saving settings. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    React.useEffect(() => {
        getSummarySettings();
    }, []);

    const handleCheckboxChange = (itemToToggle) => {
        const updatedSettings = summarySettings.map((item) => {
            if (item === itemToToggle) {
                return {
                    ...item,
                    selected: !item.selected,
                };
            }
            return item;
        });
        setSummarySettings(updatedSettings);
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Paper sx={style}>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <SettingsApplicationsIcon />
                        <H id="modal-modal-title" variant="h6" component="h2">
                            Edit weekly summary settings
                        </H>
                    </Stack>
                    <P id="modal-modal-description" sx={{ mt: 2 }}>
                        Select metrics from the platform to be included in your weekly summary
                    </P>
                    <Grid container>
                        <Grid item mr={1}>
                            <p>Work metrics</p>
                            <FormGroup>
                                {summarySettings
                                    .filter((item) => item.category === "work")
                                    .map((item) => {
                                        return (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={item.selected}
                                                        size="small"
                                                        onChange={() => handleCheckboxChange(item)}
                                                    />
                                                }
                                                label={
                                                    <span
                                                        style={{
                                                            fontSize: "12px",
                                                            fontWeight: "200",
                                                        }}
                                                    >
                                                        {item.metric}
                                                    </span>
                                                }
                                            />
                                        );
                                    })}
                            </FormGroup>
                        </Grid>
                        <Grid item>
                            <p>Collaboration metrics</p>
                            <FormGroup>
                                {summarySettings
                                    .filter((item) => item.category === "collaboration")
                                    .map((item) => {
                                        return (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={item.selected}
                                                        size="small"
                                                        onChange={() => handleCheckboxChange(item)}
                                                    />
                                                }
                                                label={
                                                    <span
                                                        style={{
                                                            fontSize: "12px",
                                                            fontWeight: "200",
                                                        }}
                                                    >
                                                        {item.metric}
                                                    </span>
                                                }
                                            />
                                        );
                                    })}
                            </FormGroup>
                            <p>Wellbeing metrics</p>
                            <FormGroup>
                                {summarySettings
                                    .filter((item) => item.category === "wellbeing")
                                    .map((item) => {
                                        return (
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={item.selected}
                                                        size="small"
                                                        onChange={() => handleCheckboxChange(item)}
                                                    />
                                                }
                                                label={
                                                    <span
                                                        style={{
                                                            fontSize: "12px",
                                                            fontWeight: "200",
                                                        }}
                                                    >
                                                        {item.metric}
                                                    </span>
                                                }
                                            />
                                        );
                                    })}
                            </FormGroup>
                        </Grid>
                    </Grid>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={2}
                        style={{ width: "100%" }}
                    >
                        <Button color="secondary" size="medium" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={
                                isDemo || !isUserPayingCustomer ? handleClose : handleSubmitSettings
                            }
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Modal>
    );
};

export default SummarySettingsModal;
